/**
 * Tab heartbeat: detect when all app tabs were closed so we can clear auth on next open.
 * Any open tab updates a timestamp in localStorage every few seconds.
 * If the timestamp is missing or older than MAX_AGE when the app loads, we treat it as
 * "all tabs were closed" and clear localStorage so the user is logged out.
 */

export const TAB_HEARTBEAT_KEY = "app_tabs_heartbeat";
/** Per-tab: set when this tab has an active app session (cleared when tab is closed). */
export const TAB_SESSION_KEY = "app_tab_session";
/** Short-lived flag set before window.open so new tabs are not logged out. */
export const TAB_NEW_TAB_GRACE_KEY = "app_new_tab_grace";
const TAB_HEARTBEAT_INTERVAL_MS = 3000;
export const TAB_HEARTBEAT_MAX_AGE_MS = 10000;
const TAB_NEW_TAB_GRACE_MS = 15000;

/**
 * Returns true if no tab has updated the heartbeat recently (all tabs were closed).
 */
export function isTabHeartbeatStale() {
  const raw = localStorage.getItem(TAB_HEARTBEAT_KEY);
  if (!raw) return true;
  const ts = parseInt(raw, 10);
  if (!ts || Number.isNaN(ts)) return true;
  return Date.now() - ts > TAB_HEARTBEAT_MAX_AGE_MS;
}

/**
 * Returns true if this tab has ever had an active session in this tab's lifetime.
 * sessionStorage is per-tab and is cleared when the tab is closed.
 */
export function hasTabSession() {
  try {
    return !!sessionStorage.getItem(TAB_SESSION_KEY);
  } catch {
    return false;
  }
}

/**
 * Mark this tab as having an active session. Call when we're logged in and not clearing.
 */
export function setTabSession() {
  try {
    sessionStorage.setItem(TAB_SESSION_KEY, "1");
  } catch (_) {}
}

/**
 * Writes current timestamp to localStorage. Call when a tab is open and user is logged in.
 * We update even when tab is hidden so any open tab keeps the heartbeat fresh.
 */
function tick() {
  if (localStorage.getItem("user_id")) {
    localStorage.setItem(TAB_HEARTBEAT_KEY, String(Date.now()));
  }
}

/**
 * Set heartbeat to now. Call immediately after successful login so PrivateRoute
 * does not treat the session as stale when redirecting to dashboard.
 */
export function setTabHeartbeatNow() {
  localStorage.setItem(TAB_HEARTBEAT_KEY, String(Date.now()));
}

export function isWithinNewTabGrace(maxAgeMs = TAB_NEW_TAB_GRACE_MS) {
  const raw = localStorage.getItem(TAB_NEW_TAB_GRACE_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!ts || Number.isNaN(ts)) return false;
  return Date.now() - ts <= maxAgeMs;
}

export function clearNewTabGrace() {
  localStorage.removeItem(TAB_NEW_TAB_GRACE_KEY);
}

/** Call in the opener tab immediately before window.open to a protected route. */
export function markNewTabOpening() {
  setTabHeartbeatNow();
  localStorage.setItem(TAB_NEW_TAB_GRACE_KEY, String(Date.now()));
}

export function hasStoredAccessToken() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return Boolean(user?.accessToken);
  } catch {
    return false;
  }
}

/**
 * Starts the heartbeat interval. Call when Layout mounts with a logged-in user.
 * Returns a function to stop the heartbeat (call on unmount or logout).
 */
export function startTabHeartbeat() {
  tick();
  const intervalId = setInterval(tick, TAB_HEARTBEAT_INTERVAL_MS);
  return () => {
    clearInterval(intervalId);
  };
}
